import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Modal from "./Modal";

export default function AdminRevisions() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState("");
  const [pendingKey, setPendingKey] = useState(null);

  useEffect(() => {
    document.title = "Revisions · Ruby Fos";
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refresh() {
    let r;
    try {
      r = await fetch("/api/admin/revisions", { credentials: "same-origin" });
    } catch (e) {
      setStatus(`error: ${e.message || "network"}`);
      setList([]);
      return;
    }
    if (r.status === 401) {
      // Session expired — bounce to login rather than render an empty list
      // that looks like "no revisions yet".
      try {
        sessionStorage.setItem("rf_admin_returnTo", "/admin/revisions");
      } catch {}
      navigate("/admin/login", { replace: true });
      return;
    }
    if (r.ok) {
      const data = await r.json();
      setList(data.revisions || []);
    } else {
      setStatus(`error: ${r.status}`);
      setList([]);
    }
  }

  async function doRestore(key) {
    setBusy(key);
    setStatus("");
    try {
      const r = await fetch("/api/admin/restore", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key }),
      });
      if (!r.ok) {
        const txt = await r.text();
        setStatus(`error: ${r.status} ${txt}`);
      } else {
        const j = await r.json();
        setStatus(`restored at ${new Date(j.updatedAt).toLocaleString()}`);
        await refresh();
      }
    } catch (e) {
      setStatus(`error: ${e.message || "network"}`);
    } finally {
      setBusy(null);
    }
  }

  function restore(key) {
    if (busy) return;
    setPendingKey(key);
  }

  const pendingRow = pendingKey ? list?.find((r) => r.key === pendingKey) : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0c0c0c",
        color: "#f4ece0",
        fontFamily: "system-ui, -apple-system, sans-serif",
        padding: "1.5rem",
      }}
    >
      <header style={{ marginBottom: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: "1rem", alignItems: "baseline" }}>
          <Link to="/admin" style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}>
            ← admin
          </Link>
          <h1 style={{ fontSize: "1.05rem", fontWeight: 500, letterSpacing: "0.04em", margin: 0 }}>
            Revisions
          </h1>
        </div>
        <Link to="/admin/edit" style={{ color: "#888", textDecoration: "none", fontSize: "0.85rem" }}>
          editor →
        </Link>
      </header>
      {status && (
        <div style={{ marginBottom: "1rem", color: "#aaa", fontSize: "0.85rem" }}>{status}</div>
      )}
      {list === null ? (
        <div style={{ color: "#666" }}>loading…</div>
      ) : list.length === 0 ? (
        <div style={{ color: "#666" }}>No revisions yet. Save the editor at least once to start the history.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", maxWidth: 760 }}>
          <thead>
            <tr style={{ color: "#888" }}>
              <th style={th}>When</th>
              <th style={th}>Key</th>
              <th style={{ ...th, textAlign: "right" }}>Restore</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.key} style={{ borderTop: "1px solid #1a1a1a" }}>
                <td style={td}>{new Date(r.ts).toLocaleString()}</td>
                <td style={{ ...td, color: "#666", fontFamily: "ui-monospace, monospace", fontSize: "0.78rem" }}>
                  {r.key}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  <button
                    onClick={() => restore(r.key)}
                    disabled={busy === r.key}
                    style={{
                      background: "transparent",
                      border: "1px solid #2a2a2a",
                      color: "#f4ece0",
                      padding: "0.35rem 0.7rem",
                      fontSize: "0.8rem",
                      borderRadius: 3,
                      cursor: busy === r.key ? "default" : "pointer",
                      opacity: busy === r.key ? 0.5 : 1,
                    }}
                  >
                    {busy === r.key ? "restoring…" : "restore"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Modal
        open={!!pendingKey}
        title="Restore this revision?"
        confirmLabel="Restore"
        cancelLabel="Cancel"
        onCancel={() => setPendingKey(null)}
        onConfirm={() => {
          const k = pendingKey;
          setPendingKey(null);
          doRestore(k);
        }}
      >
        <p style={{ margin: 0, marginBottom: "0.5rem" }}>
          Replacing the current document with{" "}
          <code style={{ fontSize: "0.85em", color: "#aaa" }}>
            {pendingKey}
          </code>
          {pendingRow && (
            <>
              {" "}
              (saved {new Date(pendingRow.ts).toLocaleString()})
            </>
          )}
          .
        </p>
        <p style={{ margin: 0, color: "#888" }}>
          The current state is snapshotted as a new revision first, so the
          restore itself can be reversed.
        </p>
      </Modal>
    </div>
  );
}

const th = { textAlign: "left", padding: "0.5rem 0.7rem", fontWeight: 500, fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.08em" };
const td = { padding: "0.6rem 0.7rem" };
